import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "~/utils/helpers"

import { Label } from "./Label"
import { usePermissionGate, type PermissionGateProps } from "./PermissionTooltip"
import { Tooltip } from "./Tooltip"

// added forceHover and forceFocus classes to simulate hover and focus states for testing purposes. used in development components page.

const textareaVariants = cva(
  [
    "flex min-w-[200px] w-full rounded-[12px] px-3 paragraph-base !text-fg-base transition",
    "placeholder:text-fg-muted",
    "focus-visible:outline-hidden [&:focus,&.forceFocus]:outline-hidden",
    "disabled:cursor-not-allowed disabled:text-fg-disabled disabled:placeholder:text-fg-disabled",
    "read-only:cursor-default read-only:text-fg-muted",
  ],
  {
    variants: {
      size: {
        default: "min-h-[64px] py-1.5",
        small: "min-h-[48px] py-1",
      },
      color: {
        field: [
          "bg-field shadow-borders-base",
          "[&:hover,&.forceHover]:bg-field-hover",
          "[&:focus,&.forceFocus]:shadow-borders-interactive-with-active",
          "disabled:bg-disabled disabled:shadow-borders-base",
          "read-only:[&:hover,&.forceHover]:bg-field",
        ],
        "field-component": [
          "bg-field-component shadow-borders-base",
          "[&:hover,&.forceHover]:bg-field-component-hover",
          "[&:focus,&.forceFocus]:shadow-borders-interactive-with-active",
          "disabled:bg-disabled disabled:shadow-borders-base",
          "read-only:[&:hover,&.forceHover]:bg-field-component",
        ],
      },
    },
    defaultVariants: {
      size: "default",
      color: "field",
    },
  },
)

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size" | "color">,
    Omit<VariantProps<typeof textareaVariants>, "size">,
    PermissionGateProps {
  containerClassName?: string
  label?: string
  title?: string
  small?: boolean
}

/**
 * Multi-line text input — comments, notes, descriptions, freeform messages.
 * Color contrasts with the surface: `field` (default, gray fill) on white (bg-base) or in a white wrapper;
 * `field-component` (white fill) only when placed directly on a gray surface (bg-background).
 * For single-line text use Input. Pass `requires` to permission-gate the field — disables itself
 * and renders its own permission tooltip in place of the label tooltip.
 * Canonical intent: {@link ../design-system/intents.ts TEXTAREA_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      containerClassName,
      label,
      small,
      color,
      disabled,
      requires,
      permissionReason,
      allowedTitle,
      tooltipSide,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    // No default wrapper width: the field's width lives in the caller's `containerClassName`,
    // so the caller also passes `wrapperClassName` when it stretches the field.
    const { blocked, gate } = usePermissionGate({
      requires,
      permissionReason,
      // The gate's wrapper covers the label too, so a gated field's `title` belongs here
      // instead of on the label — one tooltip per control.
      allowedTitle: allowedTitle ?? props.title,
      tooltipSide,
      wrapperClassName,
    })

    const field = (
      <div className={cn("relative", containerClassName)}>
        {label && (
          <Tooltip
            title={requires === undefined ? props.title : undefined}
            className="w-[280px]"
            side="top"
          >
            <Label htmlFor={props.id}>{label}</Label>
          </Tooltip>
        )}
        <textarea
          className={cn(
            textareaVariants({ size: small ? "small" : "default", color }),
            props["aria-invalid"] &&
              "shadow-borders-error [&:focus,&.forceFocus]:shadow-borders-error",
            className,
          )}
          ref={ref}
          disabled={disabled || blocked}
          {...props}
        />
      </div>
    )

    return gate(field)
  },
)
Textarea.displayName = "Textarea"

export { Textarea, textareaVariants }
